/**
 * Image artifact — the shared image viewer.
 *
 * Renders an image centered on a switchable backdrop (a soft checker so
 * transparent PNGs read correctly, or a flat light / dark surface), scaled
 * either to fit the pane or at an explicit zoom factor.
 *
 * This is the single component both surfaces render `kind: "image"`
 * artifacts through:
 *  - the chat artifact canvas ({@link ImageCanvas}) drives `zoom` + `backdrop`
 *    from its toolbar and overlays its own dimensions badge / zoom pill /
 *    details sidebar on top;
 *  - the Library file-preview ({@link ImageRenderer}) renders it with the
 *    defaults (fit-to-pane, checker backdrop), so opening an image from the
 *    Library matches the chat canvas.
 *
 * Passing `url === undefined` renders just the backdrop — the callers use
 * that for their loading / empty states while a skeleton overlays on top.
 *
 * @module components/artifacts/image-artifact
 */

import { cn } from "@/lib/utils";

/** Backdrop surface behind the image. */
export type ImageBackdropMode = "checkered" | "light" | "dark";

export interface ImageArtifactProps {
  /** Image URL (blob: or remote). Omit to render backdrop only (loading/empty). */
  url?: string;
  /** Alt text for the `<img>`. */
  alt?: string;
  /** Natural width/height, when known — used to size explicit-zoom rendering. */
  width?: number;
  height?: number;
  /** Zoom factor. `0` (default) means fit-to-pane (object-contain). */
  zoom?: number;
  /** Backdrop surface. Defaults to the checker pattern. */
  backdrop?: ImageBackdropMode;
  /** Applied to the outer container (callers position/size with this). */
  className?: string;
}

/**
 * Shared image viewer: backdrop + centered, scalable image. See the module
 * doc for the chat vs. Library split.
 */
export function ImageArtifact({
  url,
  alt,
  width,
  height,
  zoom = 0,
  backdrop = "checkered",
  className,
}: ImageArtifactProps) {
  return (
    <div className={cn("relative overflow-hidden", className)}>
      <BackdropLayer mode={backdrop} />
      {url ? (
        <ImageStage url={url} alt={alt} width={width} height={height} zoom={zoom} />
      ) : null}
    </div>
  );
}

/**
 * Backdrop layer behind the image. Defaults to a soft checker pattern so
 * transparent PNGs read correctly; callers can flip to a flat light or dark
 * surface for high-contrast viewing.
 */
function BackdropLayer({ mode }: { mode: ImageBackdropMode }) {
  if (mode === "light") {
    return <div className="absolute inset-0 bg-muted/40" />;
  }
  if (mode === "dark") {
    return <div className="absolute inset-0 bg-slate-900" />;
  }
  return (
    <div
      className="absolute inset-0"
      style={{
        backgroundColor: "hsl(var(--muted) / 0.35)",
        backgroundImage: `
          linear-gradient(45deg, rgba(148,163,184,0.18) 25%, transparent 25%),
          linear-gradient(-45deg, rgba(148,163,184,0.18) 25%, transparent 25%),
          linear-gradient(45deg, transparent 75%, rgba(148,163,184,0.18) 75%),
          linear-gradient(-45deg, transparent 75%, rgba(148,163,184,0.18) 75%)
        `,
        backgroundSize: "18px 18px",
        backgroundPosition: "0 0, 0 9px, 9px -9px, 9px 0",
      }}
    />
  );
}

/**
 * Centered image stage. `zoom = 0` means "fit to canvas" (object-contain
 * within bounds); any other value renders at that scale relative to the
 * image's natural size, letting the parent overflow scroll handle clipping.
 */
function ImageStage({
  url,
  alt,
  width,
  height,
  zoom,
}: {
  url: string;
  alt?: string;
  width?: number;
  height?: number;
  zoom: number;
}) {
  return (
    <div
      className={cn(
        "absolute inset-0 flex items-center justify-center p-8",
        zoom !== 0 && "overflow-auto",
      )}
    >
      <div className="relative max-w-full max-h-full overflow-hidden rounded-lg shadow-2xl ring-1 ring-black/10">
        <img
          src={url}
          alt={alt || "Image artifact"}
          draggable={false}
          className={cn(
            "block bg-white select-none",
            zoom === 0 ? "max-w-full max-h-[calc(100vh-12rem)] object-contain" : "",
          )}
          style={
            zoom === 0
              ? undefined
              : {
                  width: width !== undefined ? `${width * zoom}px` : `${zoom * 100}%`,
                  height: height !== undefined ? `${height * zoom}px` : "auto",
                  maxWidth: "none",
                  maxHeight: "none",
                }
          }
        />
      </div>
    </div>
  );
}
